import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CandidateSummaryPhase,
  CandidateSummaryView,
} from '../models/candidate-summary.model';
import { CandidateSummaryService } from '../services/candidate-summary.service';
export {
  formatCandidateAssessmentDocument,
  formatCandidateSummaryText,
} from '../utils/candidate-summary-labels';

export function useCandidateSummaryFlow(applicationId: string | undefined, enabled: boolean) {
  const [phase, setPhase] = useState<CandidateSummaryPhase>('idle');
  const [summary, setSummary] = useState<CandidateSummaryView | null>(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const generationAbortRef = useRef<AbortController | null>(null);

  const loadSummary = useCallback(async () => {
    if (!applicationId || !enabled) {
      return;
    }
    setPhase('loading');
    setErrorMessage(null);
    try {
      const existing = await CandidateSummaryService.GetSummary(
        applicationId,
        (error) => {
          if (error.message === 'NOT FOUND') {
            return;
          }
          throw error;
        },
        false
      );
      if (existing?.payload) {
        setSummary(existing);
        setPhase('completed');
      } else {
        setSummary(null);
        setPhase('idle');
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Could not load assessment.';
      setErrorMessage(message);
      setPhase('failed');
    }
  }, [applicationId, enabled]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(
    () => () => {
      generationAbortRef.current?.abort();
    },
    []
  );

  const generate = useCallback(async () => {
    if (!applicationId || !enabled) {
      return;
    }
    setPhase('generating');
    setErrorMessage(null);
    setProgressMessage('Assessment generation queued.');
    generationAbortRef.current?.abort();
    const abortController = new AbortController();
    generationAbortRef.current = abortController;
    try {
      await CandidateSummaryService.RunGeneration(
        applicationId,
        setProgressMessage,
        abortController.signal
      );
      await loadSummary();
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'Assessment cancelled.') {
        return;
      }
      const message =
        error instanceof Error
          ? error.message
          : 'Assessment generation failed. Please try again.';
      setErrorMessage(message);
      setPhase('failed');
    }
  }, [applicationId, enabled, loadSummary]);

  return {
    phase,
    summary,
    progressMessage,
    errorMessage,
    generate,
    reload: loadSummary,
  };
}
